import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Award, ShieldCheck, Target, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { COMPANY, IMAGES, CERTIFICATIONS } from '../data/mock';

const PageHero = ({ eyebrow, title, subtitle }) => (
  <section className="brand-gradient relative overflow-hidden">
    <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#c7e79b]/40 blur-3xl" />
    <div className="relative max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
      <Reveal>
        <span className="text-brand font-semibold text-sm uppercase tracking-wider">{eyebrow}</span>
        <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#13251b] leading-tight">{title}</h1>
        <p className="mt-5 text-lg text-[#44564b] max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
      </Reveal>
    </div>
  </section>
);

const About = () => {
  return (
    <div>
      <Seo
        path="/about"
        title="About Us — Independent Mold & Air Quality Inspectors"
        description="Established in Waterloo (2012), 360 Mold Services provides independent, IAC2 & InterNACHI-certified mold and indoor air quality testing across Southern Ontario. We test — never remediate."
      />
      <PageHero
        eyebrow="About Us"
        title="Independent testing, built on trust"
        subtitle={`Established as an ancillary of 360 Inspection Services in Waterloo (${COMPANY.established}), we specialize in objective mold and indoor air quality investigations across ${COMPANY.region}.`}
      />

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.sunlitRoom} alt="Airy, sunlit home interior" className="rounded-[1.75rem] w-full h-[440px] object-cover shadow-xl" />
          </Reveal>
          <div>
            <Reveal>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#13251b]">Our story</h2>
              <div className="mt-5 space-y-4 text-[#4d5c52] leading-relaxed">
                <p>360 Inspection Services began in Waterloo, Ontario as a fully independent home inspection company, helping residential buyers and sellers make informed real estate decisions.</p>
                <p>360 Mold Services grew from that foundation to focus on mold-related issues for residential and commercial properties. We deliberately do <strong className="text-[#13251b]">only</strong> inspections and testing — remediation would conflict with your best interest.</p>
                <p>The result is the most technologically advanced indoor environmental assessment paired with a trusted referral list of certified remediation companies — a cost-effective solution for projects large and small.</p>
              </div>
            </Reveal>
            <Reveal delay={120} className="mt-8 grid grid-cols-3 gap-4">
              {[{ icon: Award, t: '400+ hrs training' }, { icon: ShieldCheck, t: 'IAC2 Certified' }, { icon: Target, t: 'Objective results' }].map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.t} className="bg-[#f4f9ee] rounded-xl p-4 text-center">
                    <Icon className="h-6 w-6 text-brand mx-auto" />
                    <div className="mt-2 text-sm font-semibold text-[#13251b]">{b.t}</div>
                  </div>
                );
              })}
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-[#fafcf7]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="text-3xl font-bold text-[#13251b]">Certifications &amp; training</h2>
            <p className="mt-4 text-[#5b6a60]">Our inspectors are InterNACHI &amp; IAC2 certified and work strictly within their Standards of Practice.</p>
            <ul className="mt-8 space-y-3">
              {CERTIFICATIONS.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                  <span className="text-[#3d4c43]">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <div className="dark-section text-white rounded-[1.5rem] p-8 lg:p-10 h-full flex flex-col justify-center">
              <h3 className="text-2xl font-bold">Our mission</h3>
              <p className="mt-4 text-[#c3d2c6] leading-relaxed">To provide the highest-quality work possible — driven by integrity, a solid work ethic, and a passion for staying current with the newest innovations in our industry, with care for the environment and our local community.</p>
              <p className="mt-4 text-[#c3d2c6] leading-relaxed">Our pride and personal involvement result in superior quality, professionalism and competency on every assessment.</p>
              <Button asChild className="mt-8 self-start bg-[#8bd44a] hover:bg-[#7bc23a] text-[#13251b] rounded-full px-7 h-12 font-semibold">
                <Link to="/contact">Work with us <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
